import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useForm } from 'react-hook-form';
import classNames from 'classnames/bind';
import qs from 'qs';
import FormattedPrice from 'components/FormattedPrice/FormattedPrice';
import PaymentFailedPopup from 'components/PaymentFailedPopup/PaymentFailedPopup';
import ContentLoader from 'components/ContentLoader/ContentLoader';
import PageLink from 'components/PageLink/PageLink';
import http from 'app/http';
import PrintPaymentPlanSummary from './components/PrintDirectPaymentSummary/PrintDirectPaymentSummary';
import styles from './DirectPaymentSummaryPageData.module.scss';
import { getGTMVariables, getGTMVariablesPageView } from 'common/helpers/Gtm';
import TagManager from 'react-gtm-module';
import PaymentErrorPanel from 'components/PaymentErrorPanel/PaymentErrorPanel';
import SurveyResultPopup from 'components/SurveyResultPopup/SurveyResultPopup';

type Props = {
  Model: any;
  PageData: any;
  LayoutModel?: any;
};

const cx = classNames.bind(styles);

const DirectPaymentSummaryPageData = (props: Props) => {
  const { Model, PageData, LayoutModel } = props;
  const [printModel, setPrintModel] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [content, setContentPage] = useState<any>({
    Model,
    PageData,
    LayoutModel,
  });
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState<boolean>(
    Model?.IsAdyenPaymentError || Model?.IsPaymentTokenExpired,
  );
  const [showSurveyPopup, setShowSurveyPopup] = useState(
    !Model.IsAdyenPaymentError && LayoutModel.EnableNetigateSurvey
   );
  const componentRef = useRef(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const onPrint = handleSubmit(async (data: any) => {
    try {
      setIsLoading(true);
      const response = await http.post(Model?.PrintPageUrl, qs.stringify(data));
      if (response && response.data) {
        setPrintModel(response.data);
        setIsLoading(false);
        handlePrint();
        setContentPage({
          PageData: response?.data?.PageData,
          Model: response?.data?.Model,
          LayoutModel: response?.data?.LayoutModel,
        });        
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  });

  const noPlanBtnStyle = cx({
    'u-alignCenter':
      Model?.PaymentPlan == null ||
      !Model?.PaymentPlan.NoPaymentPlan ||
      null == Model?.DataCompletenessPercent ||
      Model?.DataCompletenessPercent === 100,
    'Grid-cell': true,
    'u-md-size1of2': true,
    'ButtonMultibar-itemWrapper': true,
  });

  if (!Model && !PageData) {
    return null;
  }
  useEffect(() => {
    if (LayoutModel != undefined) {
      var userid =
        LayoutModel?.Profiles.length > 0
          ? LayoutModel?.Profiles[0].DebtReference
          : '';
      var isLoggedIn = LayoutModel?.HasProfile;
      var lang = PageData?.Language;
      var env = LayoutModel?.Environment;
      var reg = LayoutModel?.CountryCode;
      getGTMVariables(userid, isLoggedIn, lang, reg, env,LayoutModel?.IsLoggedInUserSecured, LayoutModel?.SecuredCaseStatus);
      getGTMVariablesPageView(
        window.document.title,
        window.location.href,
        window.location.pathname,
        userid,
        isLoggedIn,
        lang,
        reg,
        env,
      );
    }
  }, [LayoutModel]);

  useEffect(() => {
    var tagManagerArgs: any;
    if (!Model.IsAdyenPaymentError) {
      if (Model.TriggerPurchaseSuccessfulGtm) {
        const revenueAmount = parseFloat(
          Model?.PaymentAmount ? Model?.PaymentAmount : '0',
        );
        const paymentType =
          Model?.DiscountedDebtBalance > 0
            ? 'Settle Account'
            : 'Instant Payment';

        tagManagerArgs = {
          dataLayer: {
            event: 'purchase',
            payment_type: paymentType,
            payment_method: Model?.PaymentMethodRaw,
            checkout_name: Model?.IsUserAuthenticated
              ? Model?.DiscountedDebtBalance > 0
                ? 'Settle Account'
                : 'Single Payment - Logged In'
              : 'Single Payment - Logged Out',
              ...(Model?.IsUserAuthenticated && {is_secured_user: LayoutModel?.IsLoggedInUserSecured}),
              ...(LayoutModel?.IsLoggedInUserSecured && {secured_case_status : LayoutModel?.SecuredCaseStatus} ),
            ecommerce: {
              transaction_id: Model?.AuthorizationReference,
              affiliation: 'Hoist Finance',
              value: (revenueAmount > 0 ? revenueAmount / 100 : 0).toFixed(2),
              currency: Model?.PaymentCurrency,
              items: [
                {
                  item_id: paymentType,
                  item_name: paymentType,
                  price: (revenueAmount > 0 ? revenueAmount / 100 : 0).toFixed(
                    2,
                  ),
                  quantity: '1',
                },
              ],
            },
          },
        };
        TagManager.dataLayer(tagManagerArgs);
      }
    } else {
      tagManagerArgs = {
        dataLayer: {
          event: 'form_error',
          checkout_name: Model?.IsUserAuthenticated
            ? Model?.IsFromOffer
              ? 'Settle Account'
              : 'Single Payment - Logged In'
            : 'Single Payment - Logged Out',
          error_message: 'Failed payment',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [Model]);
  useEffect(() => {
    setContentPage({ Model, PageData, LayoutModel });
  }, [Model, PageData, LayoutModel]);

  return (
    <main className={styles.wrapper}>
      {LayoutModel?.DisableWebDeflect ? (
        <>
        {(Model?.IsAdyenPaymentError || Model?.IsPaymentTokenExpired) && (
          <PaymentErrorPanel Model={Model} PageData={PageData}/>
        )}
        </>
      ):
      (
        <>
      <div
          className={classNames(
            'modal-overlay',
            showPaymentFailedPopup ? 'is-active' : '',
          )}
        >
          <PaymentFailedPopup CallUsNowButtonText={PageData?.CallUsNowButtonText}
            PaymentErrorHeader={PageData?.PaymentErrorHeader}
            PaymentErrorInfo={PageData?.PaymentErrorInfo}
            TryAgainButtonText={PageData?.TryAgainButtonText}
            SendMessageButtonText={PageData?.SendMessageButtonText}
            handleClick={() => { setShowPaymentFailedPopup(false); }}
            IsUserAuthenticated = {Model?.IsUserAuthenticated}
            DirectPaymentFlow = {true}
            RequestCallBackButtonText = {PageData?.RequestCallBackButtonText}
            {...props} />
        </div>
        </>
      )}

      {Model?.ShowPaymentSummary && (
        <div className="Page-container PageContainer--transparent u-paddingTsm u-paddingBsm">
          <div className="paymentSuccessful u-marginTz">
            {Model?.IsAdyenPaymentPending && (
              <h2
                className="paymentSuccessful__heading"
                id="direct-payment-adyen-payment-pending-info"
              >
                {PageData?.PaymentPendingInfo}
              </h2>
            )}
            {Model?.IsPaymentPlanFinalizingError && (
              <h2
                id="direct-payment-adyen-finalizing-error"
                className="paymentSuccessful__heading"
              >
                {PageData?.PaymentPlanFinalizingError}
              </h2>
            )}
            {!Model?.IsAdyenPaymentPending &&
              !Model?.IsPaymentPlanFinalizingError && (
                <h2
                  className="paymentSuccessful__heading"
                  id="direct-payment-success-info"
                >
                  {PageData?.PaymentSuccessInfo}
                </h2>
              )}

            <div className="paymentSuccessful__content">
              {Model?.IsAdyenPaymentPending && (
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/payment-processing-bg.svg`}
                  className="paymentSuccessful__icon"
                />
              )}
              {Model?.IsPaymentPlanFinalizingError && (
                <img
                  src={`${process.env.PUBLIC_URL}images/icons/exclamationmark.svg`}
                  className="paymentSuccessful__icon"
                />
              )}
              {!Model?.IsAdyenPaymentPending &&
                !Model?.IsPaymentPlanFinalizingError && (
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/payment-complete-check.svg`}
                    className="paymentSuccessful__icon"
                  />
                )}
              <div>
                {Model?.IsPayFullBalance && !Model?.IsAdyenPaymentPending && (
                  <p className="paymentSuccessful__text">
                    {PageData?.PayFullBalanceSuccessText}
                  </p>
                )}
              </div>

              <div className="paymentSuccessful__transaction">
                <div className="paymentSuccessful__transactionData">
                  <span className="paymentSuccessful__transactionLabel">
                    {PageData?.PaymentAmount}
                  </span>
                  <span className="paymentSuccessful__transactionValue paymentSuccessful__transactionValue--bold">
                    <FormattedPrice
                      price={Model?.PaymentAmount}
                      currency={Model?.PaymentCurrency}
                    />
                  </span>
                </div>
                <div className="paymentSuccessful__transactionData">
                  <span className="paymentSuccessful__transactionLabel">
                    {PageData?.AuthorizationReference}
                  </span>
                  <span className="paymentSuccessful__transactionValue">
                    {Model?.AuthorizationReference}
                  </span>
                </div>
                {/* @*TODO: Verify is below data should are displayed*@
                        @*@if (Model?.DiscountedDebtBalance > 0)
                            {
                            <div className="paymentSuccessful__transactionData">
                                <span className="paymentSuccessful__transactionLabel">{PageData?.DebtBalance}</span>
                                <span className="paymentSuccessful__transactionValue">@Html.Money(Model?.PaymentCurrency, Model?.DebtBalance, true}</span>
                            </div>
                            }
                            <div className="paymentSuccessful__transactionData">
                                <span className="paymentSuccessful__transactionLabel">{PageData?.PaymentDate}</span>
                                <span className="paymentSuccessful__transactionValue">@Html.Formatter(p => p.FormatShortDate(Model?.PaymentDate)}</span>
                            </div>
                            @if (Model?.DiscountedDebtBalance > 0)
                            {
                            <div className="paymentSuccessful__transactionData">
                                <span className="paymentSuccessful__transactionLabel">{PageData?.DiscountedDebtBalance}</span>
                                <span className="paymentSuccessful__transactionValue">@Html.Money(Model?.PaymentCurrency, Model?.DiscountedDebtBalance, true}</span>
                            </div>

                            <div className="paymentSuccessful__transactionData">
                                <span className="paymentSuccessful__transactionLabel">{PageData?.PaymentMethod}</span>
                                <span className="paymentSuccessful__transactionValue">@Model?.PaymentMethod</span>
                            </div>
                            }*@ */}
              </div>

              {Model?.IsPrintoutsEnabled && (
                <div className={styles.printWrapper}>
                  <form>
                    <input
                      type="hidden"
                      {...register('DebtBalance')}
                      value={Model?.DebtBalance}
                    />
                    <input
                      type="hidden"
                      {...register('DebtReference')}
                      value={Model?.DebtReference}
                    />
                    <input
                      type="hidden"
                      {...register('DiscountedDebtBalance')}
                      value={Model?.DiscountedDebtBalance}
                    />
                    <input
                      type="hidden"
                      {...register('IsAdyenPaymentPending')}
                      value={Model?.IsAdyenPaymentPending}
                    />
                    <input
                      type="hidden"
                      {...register('IsPaymentPlanFinalizingError')}
                      value={Model?.IsPaymentPlanFinalizingError}
                    />
                    <input
                      type="hidden"
                      {...register('PaymentAmount')}
                      value={Model?.PaymentAmount}
                    />
                    <input
                      type="hidden"
                      {...register('PaymentCurrency')}
                      value={Model?.PaymentCurrency}
                    />
                    <input
                      type="hidden"
                      {...register('PaymentDate')}
                      value={Model?.PaymentDate}
                    />
                    <input
                      type="hidden"
                      {...register('PaymentMethod')}
                      value={Model?.PaymentMethod}
                    />
                    <input
                      type="hidden"
                      {...register('AuthorizationReference')}
                      value={Model?.AuthorizationReference}
                    />
                    <input
                      type="hidden"
                      {...register('PaymentTransactionNumber')}
                      value={Model?.PaymentTransactionNumber}
                    />
                    <button
                      type="submit"
                      className="Link-icon Link-print Print--receipt"
                      onClick={onPrint}
                    >
                      {PageData?.PrintButtonLabel}
                    </button>
                  </form>
                  {printModel && (
                    <div style={{ display: 'none' }}>
                      <PrintPaymentPlanSummary
                        ref={componentRef}
                        Model={printModel.Model}
                        PageData={printModel.PageData}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="paymentSuccessful">
            {!(Model?.IsFromOffer || Model?.IsPayFullBalance) &&
              !Model?.IsAdyenPaymentError &&
              !Model?.IsAdyenPaymentPending && (
                <div className="ButtonMultibar">
                  <div className="Grid Grid--gutterV ButtonMultibar-grid">
                    {Model?.IsUserAuthenticated && (
                      <>
                        {Model?.PaymentPlan != null &&
                          Model?.PaymentPlan.NoPaymentPlan &&
                          Model?.DiscountedDebtBalance === 0 && (
                            <div className={noPlanBtnStyle}>
                              <div className="ButtonMultibar-item">
                                <label className="ButtonMultibar ButtonMultibar--label">
                                  {PageData?.SetUpRepaymentPlanLabel}
                                </label>
                                <PageLink
                                  url={Model?.SetUpRePaymentPlanStartpage}
                                  text={PageData?.SetUpRepaymentPlanButtonLabel}
                                  className="Button"
                                />
                              </div>
                            </div>
                          )}
                        {((null != Model?.DataCompletenessPercent &&
                          Model?.DataCompletenessPercent < 100) ||
                          Model?.IsInEditMode) && (
                          <div className={noPlanBtnStyle}>
                            <div className="ButtonMultibar-item">
                              <label className="ButtonMultibar ButtonMultibar--label">
                                {PageData?.CustomerDetailsStatusInfoLabel}
                                <span className="ButtonMultibarLabel--progresstext">
                                  {Model?.DataCompletenessPercent} %{' '}
                                  {PageData?.DataCompletenessEndingLabel}
                                </span>
                              </label>
                              <PageLink
                                url={Model?.CustomerProfilePage}
                                text={PageData?.CompleteDetailsButtonLabel}
                                className="Button Button--orange Button--mobFull"
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {!Model.IsUserAuthenticated &&
                      !PageData?.HideRegistrationSection && (
                        <div
                          id="accountButtonWrapper"
                          className="Grid-cell u-md-size1of1 u-alignCenter"
                        >
                          <div className="ButtonMultibar-item">
                            <label className="ButtonMultibar ButtonMultibar--label">
                              {PageData?.RegisterAccountLabel}
                            </label>
                            <PageLink
                              url={Model?.RegisterAccountPageUrl}
                              text={PageData?.RegisterAccountButtonLabel}
                              className="Button"
                            />
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}
            <div className="paymentSuccessful__ctaWrapper">
              <PageLink
                url={Model?.ClosePageUrl}
                text={PageData?.CloseButtonLabel}
                className="Button paymentSuccessful__closeBtn"
              />
            </div>
          </div>
        </div>
      )}
      {isLoading && <ContentLoader />}
      <div
          className={classNames(
            'modal-overlay',
            showSurveyPopup ? 'is-active' : '',
          )}
        >
          <div className="modal-dialog iframe_pop">
            <SurveyResultPopup closePop={setShowSurveyPopup} source={Model?.NetigateSurveyUrl}></SurveyResultPopup>
          </div>
        </div>
    </main>
  );
};

export default DirectPaymentSummaryPageData;
